import { capitalize, IconButton, ListItemText, Typography } from "@mui/material";
import {
  ButtonRow,
  DetailsList,
  DetailsListItem,
  Form,
  formatDollarAmount,
  FormButton,
  FormStep,
  FormSubmit,
  InputField,
  Link,
  Modal,
  NumericField,
  SearchField,
  useFormWizard,
  UseFormWizardStep,
  UseFormWizardStepProps,
  UseModalProps,
  useToast,
} from "@synota-io/synota-shared-ui";
import * as yup from "yup";
import { useContractPennyTest } from "../../../api/hooks/useContractPennyTest";
import { useCallback } from "react";
import { FieldValues, useWatch } from "react-hook-form";
import { INodeUser } from "../../../api/hooks/useNodeUsers";
import { generatePath, useNavigate } from "react-router-dom";
import { CONTRACTS_DETAIL_PATH, CONTRACTS_PATH } from "../../../paths";
import { OpenInNew } from "@mui/icons-material";
import { getUrlTitle } from "../utils";
import { NodeUserDetails } from "../components/NodeUserDetails";
import { useNodeUrls } from "../../../api/hooks/useNodeUrls";
import { useNodeUsers } from "../../../api/hooks/useNodeUsers";

const schema = yup
  .object({
    supplier_node: yup
      .object({
        value: yup.string(),
        label: yup.string(),
        dashboard_url: yup.string(),
      })
      .nullable()
      .required("Supplier node is required"),
    supplier_user_email: yup
      .object({
        value: yup.string(),
        label: yup.string(),
        dashboard_url: yup.string(),
      })
      .nullable()
      .required("Supplier email is required"),
    consumer_node: yup
      .object({
        value: yup.string(),
        label: yup.string(),
        dashboard_url: yup.string(),
      })
      .nullable()
      .required("Consumer node is required"),
    consumer_user_email: yup
      .object({
        value: yup.string(),
        label: yup.string(),
        dashboard_url: yup.string(),
      })
      .nullable()
      .required("Consumer email is required"),
    contract_name: yup.string().required("Contract name is required"),
    unit_of_measure: yup
      .object({
        value: yup.string(),
        label: yup.string(),
      })
      .required("Unit of measure is required"),
    amount: yup.string().required("Amount is required"),
  })
  .required();

type FormValues = yup.InferType<typeof schema>;

const USER_FILTER_MAP = {
  supplier: (user: INodeUser) => user.is_supplier,
  consumer: (user: INodeUser) => user.is_consumer,
};

export const SelectNodeStep = ({
  name,
  isLoading,
  control,
  previous,
}: UseFormWizardStepProps<FormValues> & { name: "consumer" | "supplier"; isLoading?: boolean }) => {
  const { nodeUrls, isPending } = useNodeUrls();
  const header = capitalize(name);

  return (
    <FormStep>
      <SearchField
        fullWidth
        isLoading={isPending}
        disablePortal={false}
        header={`Select a ${header} Node`}
        name={`${name}_node`}
        placeholder={isPending ? "Loading..." : "Select node"}
        control={control}
        options={nodeUrls.map((node) => ({
          value: node.uuid,
          label: getUrlTitle(node.dashboard_url),
          dashboard_url: node.dashboard_url,
        }))}
      />

      <ButtonRow>
        <FormSubmit
          fullWidth
          isLoading={isLoading}
          label="Next"
          disabled={control.getFieldState(`${name}_node.value`).invalid}
        />
        {previous && (
          <FormButton variant="text" onClick={() => previous()}>
            Back
          </FormButton>
        )}
      </ButtonRow>
    </FormStep>
  );
};

export const SelectUserStep = ({
  name,
  isLoading,
  control,
  previous,
}: UseFormWizardStepProps<FormValues> & { name: "consumer" | "supplier"; isLoading?: boolean }) => {
  const values = useWatch({ control });
  const header = capitalize(name);

  const selectedNode = values[`${name}_node`];
  const server_uuid = selectedNode?.value || "";

  const { nodeUsers, isPending } = useNodeUsers({ server_uuid });

  const user: INodeUser | undefined = values[`${name}_user_email`] as INodeUser | undefined;

  return (
    <FormStep>
      <SearchField
        fullWidth
        isLoading={isPending || !server_uuid}
        disablePortal={false}
        header={`Select a ${header} User`}
        name={`${name}_user_email`}
        placeholder={isPending ? "Loading..." : "Select user"}
        noOptionsText={`There are no ${name} accounts on this node`}
        control={control}
        getOptionLabel={(option) =>
          typeof option !== "string" && "email" in option ? option.email : option
        }
        options={nodeUsers
          .filter((user) => user.role === "admin" && !user.admin_user_email)
          .filter(USER_FILTER_MAP[name])
          .map((user) => ({
            value: user.email,
            label: user.public_name || user.email.split("@")[0],
            caption: user.email,
            dashboard_url: selectedNode?.dashboard_url,
            ...user,
          }))}
      />

      {user && <NodeUserDetails user={user} />}

      <ButtonRow>
        <FormSubmit
          fullWidth
          isLoading={isLoading}
          label="Next"
          disabled={control.getFieldState(`${name}_user_email.value`).invalid}
        />
        {previous && (
          <FormButton variant="text" onClick={() => previous()}>
            Back
          </FormButton>
        )}
      </ButtonRow>
    </FormStep>
  );
};

export const InformationStep = ({
  isLoading,
  control,
  previous,
}: UseFormWizardStepProps<FormValues> & { isLoading?: boolean }) => {
  return (
    <FormStep>
      <InputField
        autoFocus
        header="Contract name"
        fullWidth
        control={control}
        name="contract_name"
      />

      <ButtonRow>
        <NumericField
          slotProps={{ input: { startAdornment: "$" } }}
          header="Amount"
          fullWidth
          control={control}
          name="amount"
        />
        <SearchField
          header="Unit of Measure"
          name="unit_of_measure"
          control={control}
          fullWidth
          options={["kWh", "MCF"].map((value) => ({
            value,
            label: value,
          }))}
        />
      </ButtonRow>

      <ButtonRow>
        <FormSubmit label="Review" isLoading={isLoading} fullWidth />
        {previous && (
          <FormButton variant="text" onClick={() => previous()}>
            Back
          </FormButton>
        )}
      </ButtonRow>
    </FormStep>
  );
};

const ConfirmationStep = ({
  isLoading,
  control,
  previous,
}: UseFormWizardStepProps<FormValues> & { isLoading?: boolean }) => {
  const values = useWatch({ control });

  return (
    <FormStep>
      <DetailsList dense sx={{ padding: 0 }}>
        <DetailsListItem xs title="Contract Name">
          {values.contract_name || "-"}
        </DetailsListItem>
        <DetailsListItem xs title="Unit of Measure">
          {values.unit_of_measure?.label || "-"}
        </DetailsListItem>
        <DetailsListItem xs title="Amount">
          {values.amount ? formatDollarAmount(Number(values.amount)) : "-"}
        </DetailsListItem>
        <DetailsListItem
          xs
          title="Supplier"
          secondaryAction={values.supplier_user_email?.dashboard_url}
        >
          <ListItemText>{values.supplier_user_email?.value || "-"}</ListItemText>
          <Typography variant="body4">{values.supplier_user_email?.label || "-"}</Typography>
        </DetailsListItem>
        <DetailsListItem
          xs
          title="Consumer"
          secondaryAction={values.consumer_user_email?.dashboard_url}
        >
          <ListItemText>{values.consumer_user_email?.value || "-"}</ListItemText>
          <Typography variant="body4">{values.consumer_user_email?.label || "-"}</Typography>
        </DetailsListItem>
      </DetailsList>

      <ButtonRow>
        <FormSubmit autoFocus isLoading={isLoading} fullWidth label="Confirm and Submit" />
        {previous && (
          <FormButton variant="text" onClick={() => previous()}>
            Back
          </FormButton>
        )}
      </ButtonRow>
    </FormStep>
  );
};

const steps: UseFormWizardStep<FormValues>[] = [
  [(props) => <SelectNodeStep name="supplier" {...props} />, "supplier_node"],
  [(props) => <SelectUserStep name="supplier" {...props} />, "supplier_user_email"],
  [(props) => <SelectNodeStep name="consumer" {...props} />, "consumer_node"],
  [(props) => <SelectUserStep name="consumer" {...props} />, "consumer_user_email"],
  [InformationStep, ["contract_name", "amount", "unit_of_measure"]],
  [ConfirmationStep, []],
];

export const PennyTestFormModal = ({ ...props }: UseModalProps) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { createPennyTest, isPending } = useContractPennyTest();

  const onSubmit = useCallback(
    ({
      consumer_user_email,
      contract_name,
      supplier_user_email,
      unit_of_measure,
      amount,
    }: FieldValues) =>
      createPennyTest(
        {
          consumer_user_email: consumer_user_email.value,
          contract_name,
          supplier_user_email: supplier_user_email.value,
          unit_of_measure: unit_of_measure.value,
          amount,
        },
        {
          onSuccess: ({ data }) => {
            const key = "penny-test-created-" + data.shared_uuid;
            props.onClose();
            navigate(generatePath(CONTRACTS_PATH), { state: { selectedRow: data.shared_uuid } });
            toast.success("Penny test was created", {
              key,
              autoHideDuration: 10000,
              action: (
                <IconButton
                  title="Go to contract"
                  component={Link}
                  color="inherit"
                  to={generatePath(CONTRACTS_DETAIL_PATH, { id: data.shared_uuid })}
                  onClick={() => {
                    toast.closeSnackbar(key);
                  }}
                >
                  <OpenInNew />
                </IconButton>
              ),
            });
          },
        },
      ),
    [createPennyTest, navigate, props, toast],
  );

  const {
    render,
    previous,
    next,
    formState: { errors },
  } = useFormWizard<FormValues>({
    onSubmit,
    defaultValues: {
      supplier_node: null,
      supplier_user_email: null,
      consumer_node: null,
      consumer_user_email: null,
      contract_name: "",
      unit_of_measure: { value: "kWh", label: "kWh" },
      amount: "0.05",
    } as unknown as FormValues,
    schema,
    steps,
  });

  return (
    <Modal
      preventCloseOnBackdropClick
      title="Create Penny Test Contract"
      maxWidth="sm"
      fullWidth
      {...props}
    >
      <Form onSubmit={next} width="100%">
        {render({ isLoading: isPending, previous })}
        {errors.root && (
          <Typography color="error" variant="body1">
            {errors.root.message}
          </Typography>
        )}
      </Form>
    </Modal>
  );
};
